import { type JSX, useEffect, useMemo, useRef, useState } from 'react';

import type { FolderTile, NonFolderTile, Tile } from '@schema/tiles.js';

import type { BuilderStore } from '../state.js';

import { AppLibraryPicker } from './AppLibraryPicker.js';
import { SelectField, TextField, ToggleField } from './Field.js';
import { TileCsvImport } from './TileCsvImport.js';
import { TileGridView } from './TileGridView.js';
import { TilePilotEditor } from './TilePilotEditor.js';

const VIEW_KEY = 'mobilauncher.builder.tileView.v1';
type ViewMode = 'list' | 'grid';

function loadViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'list';
  try {
    const raw = window.localStorage.getItem(VIEW_KEY);
    return raw === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
}

export interface TileGridEditorProps {
  store: BuilderStore;
}

/**
 * Does a tile match the (lowercased) search query? Searches the label
 * plus the type-specific target — package name for app, URL for web,
 * action/target for agentAction — and recurses into folder children so
 * a search for "chrome" surfaces a folder that contains a Chrome tile.
 */
function tileMatchesQuery(tile: Tile, q: string): boolean {
  const hay: string[] = [tile.type];
  if (typeof tile.label === 'string') hay.push(tile.label);
  if (tile.type === 'app') {
    hay.push(tile.packageName);
  } else if (tile.type === 'web') {
    hay.push(tile.url);
  } else if (tile.type === 'agentAction') {
    hay.push(tile.action);
    if (tile.targetPackage !== undefined) hay.push(tile.targetPackage);
  } else if (tile.type === 'folder') {
    for (const child of tile.children) {
      if (typeof child.label === 'string') hay.push(child.label);
      if (child.type === 'app') hay.push(child.packageName);
      else if (child.type === 'web') hay.push(child.url);
      else if (child.type === 'agentAction') hay.push(child.action);
    }
  }
  return hay.join(' ').toLowerCase().includes(q);
}

const AGENT_ACTIONS = ['reboot', 'clearAppData', 'runScript', 'syncNow'] as const;

/**
 * The most-used builder editor: tile grid dimensions + per-tile fields.
 *
 * Type-specific fields render via a discriminated switch on `tile.type`.
 * Changing the type requires rebuilding the tile (different fields), so
 * the type selector calls `replaceTile` with a fresh default rather than
 * a partial patch — keeps the discriminated union sound.
 */
export function TileGridEditor({ store }: TileGridEditorProps): JSX.Element {
  const profile = store.activeProfile;
  const { grid } = profile;
  const capacity = grid.columns * grid.rows;
  const tileCount = grid.tiles.length;
  const atCapacity = tileCount >= capacity;
  const [view, setView] = useState<ViewMode>(loadViewMode);
  const [query, setQuery] = useState('');

  // Client-side filter for the List view. Matches label + the type's
  // target (package / URL / action) and any folder children, so an
  // admin with a screenful of tiles can jump straight to one. Reorder
  // is suppressed while a filter is active (see TileList).
  const trimmedQuery = query.trim();
  const filteredTiles = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (q === '') return grid.tiles;
    return grid.tiles.filter((t) => tileMatchesQuery(t, q));
  }, [grid.tiles, trimmedQuery]);
  const filtering = trimmedQuery !== '';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch {
      // ignored — see autosave note in state.ts
    }
  }, [view]);

  // Clicking a tile in Grid view promotes to List view so the user can
  // edit fields immediately. We can't auto-scroll without lifting refs
  // higher, so the list re-render is the smallest viable jump for v0.
  function handleTileFocus(_id: string): void {
    setView('list');
  }

  return (
    <div className="editor">
      <header className="editor__head">
        <div className="editor__head-row">
          <div>
            <h2 className="editor__title">Tile grid</h2>
            <p className="editor__sub">
              Tiles fill cells in row-major order. Use a <code>spacer</code> for
              deliberate blanks. Length must not exceed <code>columns × rows</code>.{' '}
              <a href="https://mobilauncher.com/docs/customize#tile-grid" target="_blank" rel="noopener noreferrer" className="editor__doc-link">
                Read the docs →
              </a>
            </p>
          </div>
          <div className="view-toggle" role="radiogroup" aria-label="Tile grid view">
            <button
              type="button"
              role="radio"
              aria-checked={view === 'list'}
              className={`view-toggle__btn${view === 'list' ? ' view-toggle__btn--active' : ''}`}
              onClick={() => setView('list')}
            >
              List
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={view === 'grid'}
              className={`view-toggle__btn${view === 'grid' ? ' view-toggle__btn--active' : ''}`}
              onClick={() => setView('grid')}
            >
              Grid
            </button>
          </div>
        </div>
      </header>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Grid dimensions</legend>
        <div className="grid-dims">
          <NumberField
            label="Columns"
            value={grid.columns}
            min={3}
            max={8}
            onChange={(v) => store.patchGrid({ columns: v })}
          />
          <NumberField
            label="Rows"
            value={grid.rows}
            min={3}
            max={12}
            onChange={(v) => store.patchGrid({ rows: v })}
          />
          <div className="grid-dims__capacity">
            <span className="grid-dims__capacity-label">Capacity</span>
            <span className={`grid-dims__capacity-value${atCapacity ? ' grid-dims__capacity-value--full' : ''}`}>
              {tileCount} / {capacity}
            </span>
          </div>
        </div>
      </fieldset>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Tiles ({tileCount})</legend>
        {view === 'grid' ? (
          <>
            <TileGridView
              columns={grid.columns}
              rows={grid.rows}
              tiles={grid.tiles}
              onTilesChange={(next) => store.setTiles(next)}
              onTileFocus={handleTileFocus}
            />
            <p className="grid-view__hint">
              Drag a tile to reposition · click empty cell to add · click a
              tile to edit it in List view.
            </p>
          </>
        ) : (
          <>
            {tileCount > 0 && (
              <div className="tile-search">
                <input
                  type="search"
                  className="tile-search__input"
                  placeholder="Search tiles by label, package, or URL…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="tile-search"
                />
                {filtering && (
                  <span className="tile-search__count" data-testid="tile-search-count">
                    {filteredTiles.length} of {tileCount}
                  </span>
                )}
              </div>
            )}
            {filtering && filteredTiles.length === 0 ? (
              <p className="tile-list__empty" data-testid="tile-search-empty">
                No tiles match “{trimmedQuery}”.
              </p>
            ) : (
              <TileList
                tiles={filteredTiles}
                filtering={filtering}
                onReplace={(id, next) => store.replaceTile(id, next)}
                onRemove={(id) => store.removeTile(id)}
                onMove={(id, direction) => store.moveTile(id, direction)}
                onMoveTo={(id, target) => store.moveTileTo(id, target)}
              />
            )}
            {filtering && (
              <p className="tile-search__hint">
                Reordering is paused while searching — clear the search to drag or move tiles.
              </p>
            )}
          </>
        )}
        {tileCount === 0 && (
          <p className="tile-list__empty">No tiles yet — add one below.</p>
        )}

        <div className="add-tile" data-testid="add-tile-group">
          <span className="add-tile__label">Add tile</span>
          <div className="add-tile__buttons">
            <AddButton disabled={atCapacity} onClick={() => store.addTile(makeAppTile())}>
              + App
            </AddButton>
            <AddButton disabled={atCapacity} onClick={() => store.addTile(makeWebTile())}>
              + Web
            </AddButton>
            <AddButton disabled={atCapacity} onClick={() => store.addTile(makeFolderTile())}>
              + Folder
            </AddButton>
            <AddButton disabled={atCapacity} onClick={() => store.addTile(makeSpacer())}>
              + Spacer
            </AddButton>
            <AddButton disabled={atCapacity} onClick={() => store.addTile(makeDivider())}>
              + Divider
            </AddButton>
            <AddButton disabled={atCapacity} onClick={() => store.addTile(makeAgentTile())}>
              + Agent action
            </AddButton>
          </div>
          <TileCsvImport
            disabled={atCapacity}
            freeCapacity={capacity - tileCount}
            onAddTile={(tile) => store.addTile(tile)}
          />
          {atCapacity && (
            <p className="add-tile__hint">
              Grid is full. Increase columns or rows to add more.
            </p>
          )}
        </div>
      </fieldset>
    </div>
  );
}

/**
 * Drop position relative to a target card. `above` inserts at the target's
 * index; `below` inserts at the target's index + 1.
 */
type DropPosition = 'above' | 'below';

interface DropTarget {
  id: string;
  position: DropPosition;
}

/**
 * Owns drag state so cards can coordinate (which one is mid-drag, which one
 * is the current drop target). HTML5 native DnD: each card has a grip
 * handle that fires dragstart; the whole li is a drop target.
 *
 * Why not just use the row itself as the draggable? Text inputs inside the
 * card have their own drag-to-select-text behavior — making the whole li
 * draggable would conflict. A dedicated grip avoids that.
 */
function TileList({
  tiles,
  filtering = false,
  onReplace,
  onRemove,
  onMove,
  onMoveTo,
}: {
  tiles: Tile[];
  /** When true the list is showing a filtered subset — reorder (drag +
   *  up/down) is disabled because indices no longer map to the real
   *  grid order. */
  filtering?: boolean;
  onReplace: (id: string, next: Tile) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onMoveTo: (id: string, targetIndex: number) => void;
}): JSX.Element {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  function handleDrop(): void {
    if (draggingId === null || dropTarget === null) {
      setDraggingId(null);
      setDropTarget(null);
      return;
    }
    const targetIdx = tiles.findIndex((t) => t.id === dropTarget.id);
    if (targetIdx !== -1) {
      const insertAt = dropTarget.position === 'above' ? targetIdx : targetIdx + 1;
      onMoveTo(draggingId, insertAt);
    }
    setDraggingId(null);
    setDropTarget(null);
  }

  return (
    <ul className="tile-list" onDragEnd={() => { setDraggingId(null); setDropTarget(null); }}>
      {tiles.map((tile, idx) => {
        const isDragging = draggingId === tile.id;
        const isTarget = dropTarget?.id === tile.id;
        const dropPos = isTarget ? dropTarget.position : null;
        return (
          <li
            key={tile.id}
            className={`dnd-row${isDragging ? ' dnd-row--dragging' : ''}${
              isTarget ? ` dnd-row--drop-${dropPos}` : ''
            }`}
            onDragOver={(e) => {
              if (draggingId === null || draggingId === tile.id) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              const rect = e.currentTarget.getBoundingClientRect();
              const midY = rect.top + rect.height / 2;
              const position: DropPosition = e.clientY < midY ? 'above' : 'below';
              if (dropTarget?.id !== tile.id || dropTarget.position !== position) {
                setDropTarget({ id: tile.id, position });
              }
            }}
            onDragLeave={(e) => {
              // Only clear if leaving the li bounds entirely, not just
              // crossing into a child. relatedTarget is the element being
              // entered; null = left the window.
              const next = e.relatedTarget as Node | null;
              if (!e.currentTarget.contains(next)) {
                if (dropTarget?.id === tile.id) setDropTarget(null);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop();
            }}
          >
            <TileCard
              tile={tile}
              index={idx}
              total={tiles.length}
              reorderable={!filtering}
              onChange={(next) => onReplace(tile.id, next)}
              onRemove={() => onRemove(tile.id)}
              onMove={(direction) => onMove(tile.id, direction)}
              onDragStart={() => setDraggingId(tile.id)}
            />
          </li>
        );
      })}
    </ul>
  );
}

interface TileCardProps {
  tile: Tile;
  index: number;
  total: number;
  /** When false, the drag grip + up/down buttons are hidden (filtered view). */
  reorderable?: boolean;
  onChange: (next: Tile) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onDragStart: () => void;
}

function TileCard({ tile, index, total, reorderable = true, onChange, onRemove, onMove, onDragStart }: TileCardProps): JSX.Element {
  function changeType(nextType: Tile['type']): void {
    if (nextType === tile.type) return;
    // Rebuild with the matching default; carry over the appearance fields
    // (label + icon + showLabel) so the user doesn't lose what they typed.
    // Dividers have no appearance fields — carry from them is just a
    // safe fresh label so the target tile is well-formed.
    const isDivider = tile.type === 'divider';
    const carry: {
      label: string;
      iconRef?: string | undefined;
      iconDataUrl?: string | undefined;
      showLabel?: boolean | undefined;
    } = {
      label: isDivider ? (tile.label ?? 'New tile') : tile.label,
      ...(!isDivider && tile.iconRef !== undefined && { iconRef: tile.iconRef }),
      ...(!isDivider && tile.iconDataUrl !== undefined && { iconDataUrl: tile.iconDataUrl }),
      ...(!isDivider && tile.showLabel !== undefined && { showLabel: tile.showLabel }),
    };
    switch (nextType) {
      case 'app':         return onChange({ ...carry, type: 'app',         id: tile.id, packageName: 'com.example.app' });
      case 'web':         return onChange({ ...carry, type: 'web',         id: tile.id, url: 'https://example.com', openExternal: false });
      case 'spacer':      return onChange({ ...carry, type: 'spacer',      id: tile.id });
      case 'divider':     return onChange({ type: 'divider', id: tile.id });
      case 'agentAction': return onChange({ ...carry, type: 'agentAction', id: tile.id, action: 'syncNow' });
      case 'folder':      return onChange({
        ...carry,
        type: 'folder',
        id: tile.id,
        children: [
          { type: 'app', id: crypto.randomUUID(), label: 'App 1', packageName: 'com.example.app1' },
        ],
      });
    }
  }

  return (
    <article className="tile-card" data-testid={`tile-card-${tile.id}`}>
      <header className="tile-card__head">
        {reorderable && (
          <span
            className="tile-card__grip"
            draggable
            onDragStart={(e) => {
              // Required for Firefox; some text payload makes the drag valid.
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', tile.id);
              // Use the whole card as the drag image instead of just the grip
              // glyph, so the user sees what they're moving.
              const card = e.currentTarget.closest('.tile-card');
              if (card instanceof HTMLElement) {
                e.dataTransfer.setDragImage(card, 16, 16);
              }
              onDragStart();
            }}
            title="Drag to reorder"
            aria-label="Drag to reorder tile"
            role="button"
          >
            ⋮⋮
          </span>
        )}
        <select
          className="tile-card__type"
          value={tile.type}
          onChange={(e) => changeType(e.target.value as Tile['type'])}
          aria-label="Tile type"
        >
          <option value="app">App</option>
          <option value="web">Web</option>
          <option value="folder">Folder</option>
          <option value="spacer">Spacer</option>
          <option value="divider">Divider</option>
          <option value="agentAction">Agent action</option>
        </select>
        <input
          className="tile-card__label-input"
          value={tile.label ?? ''}
          onChange={(e) => onChange({ ...tile, label: e.target.value })}
          placeholder={tile.type === 'divider' ? 'Divider (label is for screen readers)' : 'Tile label'}
          aria-label="Tile label"
        />
        <div className="tile-card__buttons">
          {reorderable && (
            <>
              <button
                type="button"
                className="tile-card__btn"
                onClick={() => onMove('up')}
                disabled={index === 0}
                aria-label="Move up"
                title="Move up"
              >
                ↑
              </button>
              <button
                type="button"
                className="tile-card__btn"
                onClick={() => onMove('down')}
                disabled={index === total - 1}
                aria-label="Move down"
                title="Move down"
              >
                ↓
              </button>
            </>
          )}
          <button
            type="button"
            className="tile-card__btn tile-card__btn--danger"
            onClick={onRemove}
            aria-label="Remove tile"
            title="Remove"
          >
            ×
          </button>
        </div>
      </header>
      <div className="tile-card__body">
        {tile.type !== 'spacer' && tile.type !== 'divider' && (
          <TileAppearance tile={tile} onChange={onChange} />
        )}
        <TileTypeFields tile={tile} onChange={onChange} />
        {tile.type !== 'spacer' && tile.type !== 'divider' && (
          <TilePilotEditor tile={tile} onChange={onChange} />
        )}
      </div>
    </article>
  );
}

/**
 * Per-tile visuals: icon source + label visibility.
 *
 * Icon sources are mutually exclusive — picking one clears the others so
 * the runtime's resolution order stays predictable (data URL > ref >
 * generated avatar). Uploads run through FileReader and store the base64
 * data URL inline on the tile; the schema caps it at ~1MB.
 */
/**
 * Exported so other editors (FeaturedTilesEditor) can drop in the same
 * icon-picker + show-label control. Accepts the full Tile union; callers
 * working with the narrower NonFolderTile cast the `next` payload back
 * to their narrower type — the appearance fields (iconRef, iconDataUrl,
 * showLabel, label) are shared across all tile variants so the cast is
 * safe.
 */
/**
 * Tiles that carry icon + label appearance fields. Positive list
 * rather than Exclude<Tile, …> because the Tile union comes wrapped
 * in a superRefine which interferes with Exclude's distribution.
 * Spacer (blank cell) and divider (row-rule) have no icon/showLabel
 * to edit, so they're not in this list — JSX call sites narrow
 * via `tile.type !== 'spacer' && tile.type !== 'divider'` before
 * passing.
 */
export type AppearanceTile =
  | Extract<Tile, { type: 'app' }>
  | Extract<Tile, { type: 'web' }>
  | Extract<Tile, { type: 'agentAction' }>
  | Extract<Tile, { type: 'folder' }>;

export function TileAppearance({
  tile,
  onChange,
}: {
  tile: AppearanceTile;
  onChange: (next: Tile) => void;
}): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const hasUpload = tile.iconDataUrl !== undefined;
  const hasRef = tile.iconRef !== undefined;
  const mode: 'generated' | 'upload' | 'ref' = hasUpload ? 'upload' : hasRef ? 'ref' : 'generated';

  function setMode(next: 'generated' | 'upload' | 'ref'): void {
    setUploadError(null);
    if (next === mode) return;
    // Clear competing fields so resolution order is unambiguous.
    const { iconDataUrl: _u, iconRef: _r, ...rest } = tile;
    void _u; void _r;
    if (next === 'generated') {
      onChange(rest as Tile);
    } else if (next === 'upload') {
      onChange(rest as Tile);
      fileInputRef.current?.click();
    } else {
      onChange({ ...rest, iconRef: '' } as Tile);
    }
  }

  async function handleFile(file: File): Promise<void> {
    setUploadError(null);
    if (!file.type.startsWith('image/')) {
      setUploadError('File must be an image.');
      return;
    }
    if (file.size > 768_000) {
      setUploadError(`Image is ${(file.size / 1024).toFixed(0)}KB — please keep icons under 750KB.`);
      return;
    }
    const dataUrl = await readAsDataUrl(file);
    const { iconRef: _r, ...rest } = tile;
    void _r;
    onChange({ ...rest, iconDataUrl: dataUrl } as Tile);
  }

  return (
    <fieldset className="tile-card__appearance">
      <legend className="tile-card__appearance-label">Appearance</legend>

      <div className="icon-picker">
        <span className="icon-picker__preview" aria-hidden>
          {tile.iconDataUrl !== undefined ? (
            <img className="icon-picker__img" src={tile.iconDataUrl} alt="" />
          ) : tile.iconRef !== undefined && tile.iconRef.length > 0 ? (
            <span className="icon-picker__ref">ref</span>
          ) : (
            <GeneratedSwatch label={tile.label} />
          )}
        </span>

        <div className="icon-picker__modes" role="radiogroup" aria-label="Icon source">
          <ModeButton active={mode === 'generated'} onClick={() => setMode('generated')}>
            Letter
          </ModeButton>
          <ModeButton active={mode === 'upload'} onClick={() => setMode('upload')}>
            Upload
          </ModeButton>
          <ModeButton active={mode === 'ref'} onClick={() => setMode('ref')}>
            Asset ref
          </ModeButton>
        </div>
      </div>

      {mode === 'upload' && (
        <div className="icon-picker__upload">
          <button
            type="button"
            className="icon-picker__upload-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            {tile.iconDataUrl !== undefined ? 'Replace image…' : 'Choose image…'}
          </button>
          {tile.iconDataUrl !== undefined && (
            <button
              type="button"
              className="icon-picker__upload-btn icon-picker__upload-btn--ghost"
              onClick={() => {
                const { iconDataUrl: _u, ...rest } = tile;
                void _u;
                onChange(rest as Tile);
              }}
            >
              Clear
            </button>
          )}
          {uploadError !== null && (
            <span className="icon-picker__error" role="alert">{uploadError}</span>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="icon-picker__file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
      />

      {mode === 'ref' && (
        <TextField
          label="Asset ref"
          mono
          hint="Path inside the asset bundle (e.g., icons/browser.png)."
          value={tile.iconRef ?? ''}
          onChange={(value) => onChange({ ...tile, iconRef: value })}
        />
      )}

      <ToggleField
        label="Show label under icon"
        hint="When off, the tile renders icon-only."
        value={tile.showLabel !== false}
        onChange={(v) => {
          if (v) {
            // Default behavior — drop the explicit field rather than store `true`.
            const { showLabel: _s, ...rest } = tile;
            void _s;
            onChange(rest as Tile);
          } else {
            onChange({ ...tile, showLabel: false });
          }
        }}
      />
    </fieldset>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      className={`icon-picker__mode${active ? ' icon-picker__mode--active' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function GeneratedSwatch({ label }: { label: string }): JSX.Element {
  // Same palette + hash as the runtime so the preview matches what ships.
  const palette = [
    '#1976d2', '#03a9b3', '#7c4dff', '#e85d75', '#f57c00', '#43a047',
    '#5e35b1', '#0288d1', '#d81b60', '#fb8c00', '#00897b', '#3949ab',
  ];
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) | 0;
  const color = palette[Math.abs(hash) % palette.length] ?? palette[0]!;
  const initial = (label.trim().charAt(0) || '?').toUpperCase();
  return (
    <span className="icon-picker__swatch" style={{ background: color }}>
      {initial}
    </span>
  );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error ?? new Error('FileReader failed'));
    fr.readAsDataURL(file);
  });
}

/** The web member of the Tile union — shared by grid + featured editors. */
export type WebTileShape = Extract<Tile, { type: 'web' }>;

/**
 * Shared editor for a web tile's URL + open-behavior controls. Used by
 * both the main tile grid and the hero featured-tiles editor so the
 * "open in external browser" / "new vs same tab" behavior is authored
 * identically in both places.
 *
 * The tab selector only appears when "open externally" is on — in the
 * in-WebView path there's no tab concept (the launcher is a single
 * WebView). Undefined externalTab shows as 'new', matching how every
 * host treats the absent value.
 */
export function WebTileFields({
  tile,
  onChange,
}: {
  tile: WebTileShape;
  onChange: (next: WebTileShape) => void;
}): JSX.Element {
  return (
    <>
      <TextField
        label="URL"
        mono
        hint="HTTPS URL to open."
        value={tile.url}
        onChange={(value) => onChange({ ...tile, url: value })}
      />
      <ToggleField
        label="Open in external browser"
        hint="Off: opens inside the launcher's WebView. On: hands the URL to Chrome via Android intent (kiosk shell / MDM only — a plain browser ignores this and navigates in place)."
        value={tile.openExternal}
        onChange={(openExternal) => onChange({ ...tile, openExternal })}
      />
      {tile.openExternal && (
        <SelectField<'new' | 'same'>
          label="Open in"
          hint="New tab forces a fresh Chrome tab. Same tab reuses the launcher's existing Chrome tab."
          value={tile.externalTab ?? 'new'}
          options={[
            { value: 'new', label: 'New tab' },
            { value: 'same', label: 'Same tab' },
          ]}
          onChange={(externalTab) => onChange({ ...tile, externalTab })}
        />
      )}
    </>
  );
}

function TileTypeFields({
  tile,
  onChange,
}: {
  tile: Tile;
  onChange: (next: Tile) => void;
}): JSX.Element | null {
  switch (tile.type) {
    case 'app':
      return <AppTileFields tile={tile} onChange={onChange} />;
    case 'web':
      return <WebTileFields tile={tile} onChange={onChange} />;
    case 'spacer':
      return <p className="tile-card__hint">Spacers occupy a grid cell but render as blank.</p>;
    case 'divider':
      return <DividerFields tile={tile} onChange={onChange} />;
    case 'agentAction':
      return (
        <>
          <label className="field">
            <span className="field__label">Action</span>
            <select
              className="field__input"
              value={tile.action}
              onChange={(e) =>
                onChange({ ...tile, action: e.target.value as (typeof AGENT_ACTIONS)[number] })
              }
            >
              {AGENT_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <span className="field__hint">
              Routed through the device's MDM agent. Pro tier required at runtime.
            </span>
          </label>
          {tile.action === 'runScript' && (
            <TextField
              label="Script ref"
              mono
              hint="Required for runScript — AssetRef resolved at deploy time."
              value={tile.scriptRef ?? ''}
              onChange={(value) => onChange({ ...tile, scriptRef: value })}
            />
          )}
          {tile.action === 'clearAppData' && (
            <TextField
              label="Target package"
              mono
              hint="Optional — package whose data to clear. Empty = launcher itself."
              value={tile.targetPackage ?? ''}
              onChange={(value) => {
                if (value.length > 0) {
                  onChange({ ...tile, targetPackage: value });
                } else {
                  const { targetPackage: _omit, ...rest } = tile;
                  void _omit;
                  onChange(rest);
                }
              }}
            />
          )}
        </>
      );
    case 'folder':
      return <FolderChildrenEditor folder={tile} onChange={onChange} />;
  }
}

/**
 * Editor for a divider tile — color + height. Both optional in the
 * schema; the empty / blank state falls back to the launcher's
 * --ml-accent and 8px respectively.
 */
/**
 * App-tile inspector: the package-name field plus a "Pick from library"
 * button that opens AppLibraryPicker. Picking an entry overwrites the
 * package name + auto-fills the label IF it currently equals the
 * default placeholder ("New tile", "App 1", etc.) so a fresh add-tile
 * → pick-from-library is one click; a customer who already named the
 * tile keeps their custom label.
 */
function AppTileFields({
  tile,
  onChange,
}: {
  tile: Extract<Tile, { type: 'app' }>;
  onChange: (next: Tile) => void;
}): JSX.Element {
  const [pickerOpen, setPickerOpen] = useState(false);
  const PLACEHOLDER_LABELS = new Set(['App 1', 'New tile', 'Feature App 1', 'Feature App 2', 'Feature App 3', 'Feature App 4']);
  return (
    <>
      <div className="field-with-action">
        <TextField
          label="Package name"
          mono
          hint="Android app package name, e.g., com.android.chrome. Pick from the library →, or find one via Play Store URLs / `pm list packages`."
          value={tile.packageName}
          onChange={(value) => onChange({ ...tile, packageName: value })}
        />
        <button
          type="button"
          className="builder__btn builder__btn--ghost field-with-action__btn"
          onClick={() => setPickerOpen(true)}
          data-testid={`tile-${tile.id}-app-library`}
        >
          📚 Pick from library
        </button>
      </div>
      {pickerOpen && (
        <AppLibraryPicker
          onClose={() => setPickerOpen(false)}
          onPick={(entry) => {
            const isPlaceholderLabel = PLACEHOLDER_LABELS.has(tile.label);
            onChange({
              ...tile,
              packageName: entry.packageName,
              ...(isPlaceholderLabel ? { label: entry.label } : {}),
            });
          }}
        />
      )}
    </>
  );
}

function DividerFields({
  tile,
  onChange,
}: {
  tile: Extract<Tile, { type: 'divider' }>;
  onChange: (next: Tile) => void;
}): JSX.Element {
  function patch(next: Partial<Extract<Tile, { type: 'divider' }>>): void {
    onChange({ ...tile, ...next });
  }
  return (
    <>
      <label className="field">
        <span className="field__label">Color</span>
        <input
          className="field__input"
          type="color"
          value={tile.color ?? '#03DAC6'}
          onChange={(e) => patch({ color: e.target.value })}
        />
        <span className="field__hint">
          Bar color. Leave on the default to follow the launcher's
          accent (changes when you re-theme).
        </span>
      </label>
      <label className="field">
        <span className="field__label">Height (px)</span>
        <input
          className="field__input"
          type="number"
          min={2}
          max={16}
          step={1}
          value={tile.height ?? 8}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (Number.isFinite(n)) patch({ height: Math.min(16, Math.max(2, Math.round(n))) });
          }}
        />
        <span className="field__hint">Thickness of the bar. 2–16 px.</span>
      </label>
      <p className="tile-card__hint">
        Dividers span every column of their row. Place between the
        tiles you want visually separated.
      </p>
    </>
  );
}

/* ───── Folder children editor ───── */

/** Schema's hard cap on children per folder — matches FolderTile in src/schema/tiles.ts. */
const MAX_FOLDER_CHILDREN = 16;

/**
 * Visual editor for a folder tile's children. The schema bans nested
 * folders, so the type picker here only offers App / Web / Spacer /
 * Agent action. min: 1 child is enforced by disabling Remove when
 * `children.length === 1`; max: 16 is enforced by disabling Add when
 * the array is at capacity. Reuses TileTypeFields + TileAppearance
 * for each child so the field UI matches the top-level tile editor.
 */
function FolderChildrenEditor({
  folder,
  onChange,
}: {
  folder: FolderTile;
  onChange: (next: Tile) => void;
}): JSX.Element {
  const children = folder.children;
  const atMin = children.length <= 1;
  const atMax = children.length >= MAX_FOLDER_CHILDREN;

  function replaceChild(idx: number, next: NonFolderTile): void {
    const nextChildren = children.slice();
    nextChildren[idx] = next;
    onChange({ ...folder, children: nextChildren });
  }
  function removeChild(idx: number): void {
    if (atMin) return;
    const nextChildren = children.slice();
    nextChildren.splice(idx, 1);
    onChange({ ...folder, children: nextChildren });
  }
  function moveChild(idx: number, direction: 'up' | 'down'): void {
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= children.length) return;
    const nextChildren = children.slice();
    const [moved] = nextChildren.splice(idx, 1);
    if (!moved) return;
    nextChildren.splice(target, 0, moved);
    onChange({ ...folder, children: nextChildren });
  }
  function addChild(type: NonFolderTile['type']): void {
    if (atMax) return;
    onChange({ ...folder, children: [...children, makeChild(type)] });
  }
  function changeChildType(idx: number, nextType: NonFolderTile['type']): void {
    const existing = children[idx];
    if (!existing || existing.type === nextType) return;
    // Carry label + icon across so the user doesn't lose what they typed.
    const carry: {
      label: string;
      iconRef?: string | undefined;
      iconDataUrl?: string | undefined;
      showLabel?: boolean | undefined;
    } = {
      label: existing.label,
      ...(existing.iconRef !== undefined && { iconRef: existing.iconRef }),
      ...(existing.iconDataUrl !== undefined && { iconDataUrl: existing.iconDataUrl }),
      ...(existing.showLabel !== undefined && { showLabel: existing.showLabel }),
    };
    let next: NonFolderTile;
    switch (nextType) {
      case 'app':
        next = { ...carry, type: 'app', id: existing.id, packageName: 'com.example.app' };
        break;
      case 'web':
        next = { ...carry, type: 'web', id: existing.id, url: 'https://example.com', openExternal: false };
        break;
      case 'spacer':
        next = { ...carry, type: 'spacer', id: existing.id };
        break;
      case 'agentAction':
        next = { ...carry, type: 'agentAction', id: existing.id, action: 'syncNow' };
        break;
    }
    replaceChild(idx, next);
  }

  return (
    <div className="folder-editor" data-testid={`folder-editor-${folder.id}`}>
      <p className="folder-editor__hint">
        Children open when this folder tile is tapped. Min 1, max{' '}
        {MAX_FOLDER_CHILDREN}. Nested folders aren't allowed.
      </p>
      <ul className="folder-editor__list">
        {children.map((child, idx) => (
          <li key={child.id} className="folder-editor__row">
            <header className="folder-editor__row-head">
              <select
                className="tile-card__type"
                value={child.type}
                onChange={(e) =>
                  changeChildType(idx, e.target.value as NonFolderTile['type'])
                }
                aria-label="Child tile type"
              >
                <option value="app">App</option>
                <option value="web">Web</option>
                <option value="spacer">Spacer</option>
                <option value="agentAction">Agent action</option>
              </select>
              <input
                className="tile-card__label-input"
                value={child.label}
                onChange={(e) => replaceChild(idx, { ...child, label: e.target.value })}
                placeholder="Child label"
                aria-label="Child tile label"
              />
              <div className="tile-card__btns">
                <button
                  type="button"
                  className="tile-card__btn"
                  onClick={() => moveChild(idx, 'up')}
                  disabled={idx === 0}
                  aria-label="Move up"
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="tile-card__btn"
                  onClick={() => moveChild(idx, 'down')}
                  disabled={idx === children.length - 1}
                  aria-label="Move down"
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="tile-card__btn tile-card__btn--danger"
                  onClick={() => removeChild(idx)}
                  disabled={atMin}
                  aria-label="Remove child"
                  title={atMin ? 'Folder needs at least one child' : 'Remove'}
                >
                  ×
                </button>
              </div>
            </header>
            <div className="folder-editor__row-body">
              {child.type !== 'spacer' && (
                <TileAppearance
                  tile={child as AppearanceTile}
                  onChange={(next) => replaceChild(idx, next as NonFolderTile)}
                />
              )}
              <TileTypeFields
                tile={child as Tile}
                onChange={(next) => replaceChild(idx, next as NonFolderTile)}
              />
            </div>
          </li>
        ))}
      </ul>
      <div className="folder-editor__add">
        <span className="add-tile__label">Add child</span>
        <div className="add-tile__buttons">
          <AddButton disabled={atMax} onClick={() => addChild('app')}>
            + App
          </AddButton>
          <AddButton disabled={atMax} onClick={() => addChild('web')}>
            + Web
          </AddButton>
          <AddButton disabled={atMax} onClick={() => addChild('spacer')}>
            + Spacer
          </AddButton>
          <AddButton disabled={atMax} onClick={() => addChild('agentAction')}>
            + Agent action
          </AddButton>
        </div>
        {atMax && (
          <p className="add-tile__hint">
            Folder is full ({MAX_FOLDER_CHILDREN} children max).
          </p>
        )}
      </div>
    </div>
  );
}

function makeChild(type: NonFolderTile['type']): NonFolderTile {
  switch (type) {
    case 'app':
      return { type: 'app', id: crypto.randomUUID(), label: 'New app', packageName: 'com.example.app' };
    case 'web':
      return {
        type: 'web',
        id: crypto.randomUUID(),
        label: 'New link',
        url: 'https://example.com',
        openExternal: false,
      };
    case 'spacer':
      return { type: 'spacer', id: crypto.randomUUID(), label: 'spacer' };
    case 'agentAction':
      return { type: 'agentAction', id: crypto.randomUUID(), label: 'Sync', action: 'syncNow' };
  }
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}): JSX.Element {
  return (
    <label className="field field--inline">
      <span className="field__label">{label}</span>
      <input
        className="field__input field__input--num"
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!Number.isNaN(n)) onChange(n);
        }}
      />
      <span className="field__hint">
        {min}–{max}
      </span>
    </label>
  );
}

function AddButton({
  children,
  onClick,
  disabled,
}: {
  children: string;
  onClick: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button type="button" className="add-tile__btn" onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

/* ───────── Tile factories ───────── */

function makeAppTile(): Tile {
  return {
    type: 'app',
    id: crypto.randomUUID(),
    label: 'New app',
    packageName: 'com.example.app',
  };
}
function makeWebTile(): Tile {
  return {
    type: 'web',
    id: crypto.randomUUID(),
    label: 'New link',
    url: 'https://example.com',
    openExternal: false,
  };
}
function makeSpacer(): Tile {
  return {
    type: 'spacer',
    id: crypto.randomUUID(),
    label: 'spacer',
  };
}
function makeDivider(): Tile {
  // Default to 8px tall + accent color from CSS — admins can refine
  // height + color per-tile from the editor inspector below.
  return {
    type: 'divider',
    id: crypto.randomUUID(),
  };
}
function makeAgentTile(): Tile {
  return {
    type: 'agentAction',
    id: crypto.randomUUID(),
    label: 'Sync',
    action: 'syncNow',
  };
}
function makeFolderTile(): Tile {
  return {
    type: 'folder',
    id: crypto.randomUUID(),
    label: 'New folder',
    children: [
      {
        type: 'app',
        id: crypto.randomUUID(),
        label: 'App 1',
        packageName: 'com.example.app1',
      },
    ],
  };
}
